(ns manage.content-variant-ocp.db.content-mapping
  (:require [clojure.spec.alpha :as spec]
            [clojure.string :as str]
            [common.form.core :as form]
            [common.validators.core :refer [valid-placeholder-name?]]
            [manage.content-configuration-page.placeholder.type :refer [type-option]]
            [manage.content-variant-ocp.db.core
             :refer [->external-content-mappings entity-name ui-messages]]
            [mate.core :as mc]
            [phrase.alpha :as p]
            [re-frame.core :as rf]))

(def placeholder-type-options
  (mapv type-option [:text :image :video :audio]))

;; This is a special case and has only 1 option "click", used when the field is a click-through.
(def placeholder-click-options
  [(type-option :click)])

(defn distinct-elements?
  [k vs]
  (if-not (seq vs)
    true
    (apply distinct? (mapv k vs))))

(spec/def ::name (spec/and string?
                           (complement str/blank?)
                           #(<= (count %) 70)
                           valid-placeholder-name?))

(spec/def ::content-placeholder
  (spec/keys :req-un [::name]))

(spec/def ::content-mappings (spec/and (spec/coll-of ::content-placeholder)
                                       #(distinct-elements? :name %)))

(spec/def ::value
  (spec/keys :opt-un [::content-mappings]))

(p/defphraser #(distinct-elements? k %)
  [_ {:keys [val] :as problem} k]
  (let [idxs (->> val
                  (map-indexed #(assoc %2 :idx %1))
                  (group-by k)
                  (some (fn [[_n mapping]]
                          (when (< 1 (count mapping))
                            (mapv :idx mapping)))))]
    {:message "The fields should have unique names"
     :ins (map #(concat (:in problem) [% k]) idxs)
     :problem problem}))

(def validate-form
  (rf/enrich
   (form/get-validate-form-fn :form.content-variant.content-mapping ::value)))

(rf/reg-event-fx :form.content-variant.content-mapping/auto-map-content
  [(rf/inject-cofx :sub :form.content-variant/fields)
   (rf/inject-cofx :sub :resource.template/placeholder-name->placeholder-type)]
  (fn [{:keys [db resource.template/placeholder-name->placeholder-type] :as cofx} _]
    (let [;; The fields in the feed
          field-names (get-in cofx [:form.content-variant/fields :value])
          ;; Their relation to content-mapping, when their name matches the placeholder.
          field-name->content-mapping (->> field-names
                                           (keep (fn [field-name]
                                                   (when-some [placeholder-type (-> field-name placeholder-name->placeholder-type)]
                                                     [field-name {:name field-name
                                                                  :type placeholder-type}]))))]
      {:db (update-in db [:form.content-variant.content-mapping :value :content-mappings]
                      into field-name->content-mapping)})))

(rf/reg-sub :form.content-variant.content-mapping/auto-map-disabled
  :<- [:resource.template/placeholder-name->placeholder-type]
  (fn [placeholder-name->placeholder-type _]
    (empty? placeholder-name->placeholder-type)))

(rf/reg-event-fx :form.content-variant.content-mapping/open
  (fn [_ _]
    {:fx [[:dispatch [:form.content-variant.content-mapping/init]]]}))

(rf/reg-event-fx :form.content-variant/update-field-content-mapping
  [validate-form]
  (fn [{:keys [db]} [_ payload next-tab]]
    (let [feed-id (get-in db [:resource.adset/one :value :feedId])
          content-mapping-payload (-> (:content-mappings payload)
                                      ->external-content-mappings)]
      {:fx [[:dispatch [:loader/open]]
            [:async-flow {:id :form.content-variant/update-content-mapping-flow
                          :first-dispatch [:resource.feed-importers/update-field-content-mappings feed-id content-mapping-payload]
                          :rules [{:when :seen?
                                   :events :resource.feed-importers/update-field-content-mappings-success
                                   :halt? false
                                   :dispatch [:form.content-variant/update-field-content-mapping-success next-tab]}
                                  {:when :seen?
                                   :events :resource.feed-importers/get-content-feed-success
                                   :halt? true
                                   :dispatch [:form.content-variant/get-content-feed-success]}
                                  {:when :seen?
                                   :events :resource.feed-importers/update-field-content-mappings-failure
                                   :halt? true
                                   :dispatch [:form.content-variant/update-field-content-mapping-failure]}]}]]})))

(rf/reg-event-fx :form.content-variant/update-field-content-mapping-success
  (fn [{:keys [db]} [_ next-tab]]
    (let [next-tab (next-tab)
          feed-id (get-in db [:resource.adset/one :value :feedId])]
      {:fx [[:dispatch [:loader/close]]
            [:dispatch [:response-handler/handle-response
                        {:entity-name entity-name
                         :type :success
                         :message (get-in ui-messages [:edit :success])}]]
            [:dispatch [:resource.feed-importers/get-content-feed feed-id]]
            (when (nil? next-tab) [:dispatch [:ocp/close]])]})))

(rf/reg-event-fx :form.content-variant/update-field-content-mapping-failure
  (fn [_ [event [_ response]]]
    {:fx [[:dispatch [:loader/close]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :error
                       :event event
                       :message (get-in ui-messages [:edit :failure])
                       :response response}]]]}))

(rf/reg-sub :form.content-variant/content-classifications
  :<- [:resource.feed-importers/sanitised-fields]
  :<- [:resource.feed-imports/field-classes]
  :<- [:resource.feed-importers/field-content-mappings]
  :<- [:resource.adset/current-channel]
  (fn [[field-names field-classes field-content-mappings adset-channel] _]
    (let [field-name->field-class (mc/index-by :name :class field-classes)
          ;; The set of field-names we want in our content mapping table.
          content-mapping-names-set (into #{}
                                          (keep (fn [{:keys [name]}]
                                                  (when-not (and (not= adset-channel "display")
                                                                 (= (field-name->field-class name) "click-through-key"))
                                                    name)))
                                          field-content-mappings)]
      {:value (->> field-names
                   (filterv content-mapping-names-set))})))

(def use-error-message (partial form/use-error-message :form.content-variant.content-mapping))

(def use-bind-control (partial form/use-bind-control :form.content-variant.content-mapping))

(form/register-form {:namespace :form.content-variant.content-mapping
                     :validate-form validate-form
                     :submit-event :form.content-variant/update-field-content-mapping})
